import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import paymentService from '../services/paymentService';
import PaymentLinkGenerator from './PaymentLinkGenerator';

const PaymentDashboard = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('generator');
  const [pendingPayments, setPendingPayments] = useState([]);
  const [approvedPayments, setApprovedPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (activeTab === 'pending' || activeTab === 'approved') {
      fetchPayments();
    }
  }, [activeTab]);
  
  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (activeTab === 'pending') {
        const payments = await paymentService.getPendingPayments();
        setPendingPayments(payments);
      } else if (activeTab === 'approved') {
        const payments = await paymentService.getApprovedPayments();
        setApprovedPayments(payments);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payments. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  const handleApprovePayment = async (paymentId) => {
    try {
      setLoading(true);
      await paymentService.approvePayment(paymentId);
      
      // Refresh data
      fetchPayments();
      
      // Show success message
      setError(null);
    } catch (err) {
      console.error('Error approving payment:', err);
      setError('Failed to approve payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Payment Dashboard</h1>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p>{error}</p>
          <button 
            className="text-red-700 font-bold ml-2"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex -mb-px">
          <button
            onClick={() => setActiveTab('generator')}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'generator'
                ? 'border-b-2 border-purple-500 text-purple-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Payment Generator
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-b-2 border-purple-500 text-purple-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pending Payments
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'approved'
                ? 'border-b-2 border-purple-500 text-purple-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Approved Payments
          </button>
          <button
            onClick={() => setActiveTab('distribution')}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'distribution'
                ? 'border-b-2 border-purple-500 text-purple-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Payment Distribution
          </button>
        </div>
      </div>
      
      {/* Active Tab Content */}
      {activeTab === 'generator' && (
        <div>
          <PaymentLinkGenerator />
        </div>
      )}
      
      {activeTab === 'pending' && (
        <div>
          <h2 className="text-xl font-bold mb-4">Pending Payments</h2>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : pendingPayments.length === 0 ? (
            <p className="text-gray-500">No pending payments found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-600">ID</th>
                    <th className="px-4 py-2 text-left text-gray-600">Amount (USD)</th>
                    <th className="px-4 py-2 text-left text-gray-600">Status</th>
                    <th className="px-4 py-2 text-left text-gray-600">Created At</th>
                    <th className="px-4 py-2 text-left text-gray-600">Payer</th>
                    <th className="px-4 py-2 text-left text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingPayments.map(payment => (
                    <tr key={payment.id} className="border-b">
                      <td className="px-4 py-2">{payment.id}</td>
                      <td className="px-4 py-2">${parseFloat(payment.amount_usd).toFixed(2)}</td>
                      <td className="px-4 py-2 capitalize">{payment.status}</td>
                      <td className="px-4 py-2">{formatDate(payment.created_at)}</td>
                      <td className="px-4 py-2">{payment.payer_email || 'N/A'}</td>
                      <td className="px-4 py-2">
                        {payment.status === 'paid' && (
                          <button
                            onClick={() => handleApprovePayment(payment.id)}
                            disabled={loading}
                            className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                          >
                            Approve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'approved' && (
        <div>
          <h2 className="text-xl font-bold mb-4">Approved Payments</h2>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : approvedPayments.length === 0 ? (
            <p className="text-gray-500">No approved payments found.</p>
          ) : (
            <div className="space-y-6">
              {approvedPayments.map(payment => (
                <div key={payment.id} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 p-4">
                    <h3 className="font-bold">
                      Payment #{payment.id} - ${parseFloat(payment.amount_usd).toFixed(2)} USD
                    </h3>
                    <p className="text-sm text-gray-600">
                      {parseFloat(payment.amount_sol).toFixed(4)} SOL | 
                      Exchange Rate: ${parseFloat(payment.exchange_rate).toFixed(2)} |
                      Approved: {formatDate(payment.approved_at)}
                    </p>
                  </div>
                  
                  {/* Distributions */}
                  {payment.distributions && payment.distributions.length > 0 && (
                    <div className="p-4">
                      <h4 className="font-bold mb-2">Distributions</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-4 py-2 text-left text-gray-600">ID</th>
                              <th className="px-4 py-2 text-left text-gray-600">Wallet</th>
                              <th className="px-4 py-2 text-left text-gray-600">Amount (SOL)</th>
                              <th className="px-4 py-2 text-left text-gray-600">Status</th>
                              <th className="px-4 py-2 text-left text-gray-600">Transaction Hash</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payment.distributions.map(dist => (
                              <tr key={dist.id} className="border-b">
                                <td className="px-4 py-2">{dist.id}</td>
                                <td className="px-4 py-2">
                                  {dist.wallet_name || 'N/A'}<br />
                                  <span className="text-xs text-gray-500">
                                    {dist.wallet_address ? (
                                      `${dist.wallet_address.substring(0, 6)}...${dist.wallet_address.substring(dist.wallet_address.length - 4)}`
                                    ) : 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-2">{parseFloat(dist.amount_sol).toFixed(4)}</td>
                                <td className="px-4 py-2 capitalize">{dist.status}</td>
                                <td className="px-4 py-2">
                                  {dist.tx_hash ? (
                                    <a 
                                      href={`https://explorer.solana.com/tx/${dist.tx_hash}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline"
                                    >
                                      {dist.tx_hash.substring(0, 8)}...
                                    </a>
                                  ) : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'distribution' && (
        <div>
          <iframe 
            src="/payment-distribution" 
            className="w-full h-screen border-none"
            title="Payment Distribution"
          />
        </div>
      )}
    </div>
  );
};

export default PaymentDashboard;
